import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { CheckCircle2, MessageCircle, ClipboardCheck, Clock } from "lucide-react";
import { IMG } from "./shared";
import { Embers } from "./Embers";
import { WHATSAPP_URL } from "@/lib/api";
import { useSiteConfig, fullUrl } from "@/context/SiteConfigContext";

const HERO_SLIDES = [
  IMG.hero,
  "/assets/serv-fogao.jpg",
  "/assets/serv-aquecedor.jpg",
  "/assets/serv-estanqueidade.jpg",
  "/assets/serv-tubulacao.jpg",
  "/assets/serv-laudos.jpg",
  "/assets/serv-manutencao.jpg",
];

const SLIDE_VARIANTS = [
  { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
  { initial: { opacity: 0, x: 70 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -70 } },
  { initial: { opacity: 0, scale: 1.12 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.95 } },
  { initial: { opacity: 0, y: 50 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -50 } },
];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const CHECKS = [
  "Atendimento rápido",
  "Técnicos certificados",
  "Residências, condomínios e empresas",
  "Atendimento em São Paulo e Região",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [isDesktop, setIsDesktop] = useState(false);
  const [slide, setSlide] = useState(0);
  const [variant, setVariant] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const cfg = useSiteConfig();
  const badge = cfg.hero_badge || "Atendimento em São Paulo e Região";
  const line1 = cfg.hero_line1 || "Vazamento de Gás?";
  const line2 = cfg.hero_line2 || "Não espere o";
  const line3 = cfg.hero_line3 || "problema aumentar.";
  const subtitle =
    cfg.hero_subtitle ||
    "Detectamos e eliminamos vazamentos de gás com equipamentos profissionais, seguindo a NBR 15526. Serviços seguros e certificados para residências, comércios e indústrias.";
  const slides = (cfg.hero_slides?.length ? cfg.hero_slides : HERO_SLIDES).map(fullUrl);

  useEffect(() => {
    const t = setInterval(() => {
      setSlide((s) => (s + 1) % slides.length);
      setVariant(Math.floor(Math.random() * SLIDE_VARIANTS.length));
    }, 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="hero-grid hero-fire relative overflow-hidden bg-[var(--dark-bg)] pb-20 pt-28 sm:pb-24 lg:pb-28 lg:pt-44">
      <Embers />
      <div className="pointer-events-none absolute -top-40 left-1/3 h-[480px] w-[480px] rounded-full bg-[var(--primary)]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 sm:gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-8">
        <motion.div style={{ y: textY }}>
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--primary)]" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[var(--primary)]">
              {badge}
            </span>
          </motion.div>

          <h1 className="font-display text-[2.6rem] font-extrabold uppercase leading-[0.92] text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <MaskLine delay={0.1}>{line1}</MaskLine>
            <MaskLine delay={0.25}>
              <span className="italic text-[var(--primary)]">{line2}</span>
            </MaskLine>
            <MaskLine delay={0.4}>
              <span className="italic text-[var(--primary)]">{line3}</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-7 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-2"
            data-testid="hero-checks"
          >
            {CHECKS.map((c) => (
              <div key={c} className="flex items-center gap-2.5 text-sm text-zinc-300">
                <CheckCircle2 size={17} className="shrink-0 text-[var(--primary)]" />
                {c}
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="group flex items-center justify-center gap-2.5 rounded-full bg-[var(--whatsapp)] px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-black shadow-[0_8px_40px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:scale-105"
            >
              <MessageCircle size={19} strokeWidth={2.5} className="transition-transform duration-300 group-hover:-rotate-12" />
              Chamar no WhatsApp
            </a>
            <a
              href="#contato"
              data-testid="hero-visit-button"
              className="flex items-center justify-center gap-2.5 rounded-full border border-zinc-600 px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-[var(--primary)] hover:text-[var(--primary)]"
            >
              <ClipboardCheck size={19} />
              Solicitar Visita Técnica
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={isDesktop ? { y: imgY } : undefined} className="relative mb-8 lg:mb-0" data-testid="hero-image-wrapper">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-[var(--primary)]/15 blur-3xl" />
            <div className="relative overflow-hidden rounded-[1.6rem] border border-white/10 bg-[#12141A] shadow-2xl">
              <div className="relative h-[300px] sm:h-[420px] lg:h-[540px]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={slide}
                    src={slides[slide]}
                    alt="Trabalhos de gás encanado da Manutentudo"
                    data-testid="hero-image"
                    variants={SLIDE_VARIANTS[variant]}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--dark-bg)]/70 via-transparent to-transparent" />
              <div className="absolute bottom-3 right-3 flex gap-1.5" data-testid="hero-slide-dots">
                {slides.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => { setSlide(i); setVariant(Math.floor(Math.random() * SLIDE_VARIANTS.length)); }}
                    data-testid={`hero-slide-dot-${i}`}
                    aria-label={`Foto ${i + 1}`}
                    className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
                      i === slide ? "w-5 bg-[var(--primary)]" : "w-1.5 bg-white/40 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 left-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#12141A]/95 px-4 py-3 shadow-2xl backdrop-blur sm:px-5 sm:py-4 lg:-bottom-6 lg:-left-8"
              data-testid="hero-floating-card"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--primary)]/15">
                <Clock size={20} className="text-[var(--primary)]" />
              </span>
              <div>
                <p className="font-display text-xl font-bold leading-none text-white">24h</p>
                <p className="text-xs text-zinc-400">Atendimento emergencial</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
